import React, { CSSProperties, useMemo } from 'react';
import { RELEASE_THEMES, themeColours, themeType } from 'fym-shared/src/music24/releaseThemes';
import {
  buildLyricMomentRow,
  LYRIC_MOMENT_COLUMN,
  LYRIC_MOMENT_LYRIC_PX,
} from 'fym-shared/src/music24/story/lyricMomentVideo';
import { storyFrameOf } from 'fym-shared/src/music24/story/storyFrames';
import { storyDisplayShadow } from 'fym-shared/src/music24/story/storyStyles';
import { TypedMemo } from '../../../../common/types';
import { kitFontName, useKitWebFonts } from '../../themes/kitWebFonts';
import type { StoryPreviewContext } from '../details/imageTiles/storyPreviewContext';
import type { FrameLyric } from './assetFrameVars';
import { StoryPreview } from './StoryPreview';

interface LyricMomentPreviewProps {
  context: StoryPreviewContext;
  /** The picked line (`current`) and the line after it; none: the first lyric line of the song. */
  lyric: FrameLyric | undefined;
  width: number;
}

const CANVAS = { width: 1080, height: 1920 } as const;

/**
 * The step-3 preview of a lyric moment (design `asset-redesign/u`): one frame of the render, drawn
 * with its shared tables. The cover row, the CTA pill by the date and "Link in bio" with the
 * SmartLink come from `storyLayout` (format `moment916`, `StoryPreview`); the lyric column shows
 * the picked line as the sung line (its foot at y 710, at 104 px times the display factor of the
 * theme, in the artist's own case) and the next line under it at the dim scale and alpha of the
 * theme lyric motion.
 */
const LyricMomentPreviewComponent: React.FC<LyricMomentPreviewProps> = ({ context, lyric, width }) => {
  useKitWebFonts();
  const { look, palette } = context;
  const { themeId } = look;
  const type = useMemo(() => themeType(look), [look]);
  const colours = useMemo(() => themeColours(themeId, palette), [palette, themeId]);
  const story = useMemo(
    () =>
      buildLyricMomentRow({
        title: context.title,
        artist: context.artist,
        releaseDate: context.releaseDate,
        today: context.today,
        link: context.link,
        presave: context.presave,
        locale: context.locale,
      }),
    [context.artist, context.link, context.locale, context.presave, context.releaseDate, context.title, context.today]
  );
  const motion = RELEASE_THEMES[themeId].brief.lyricMotion;
  const frame = storyFrameOf(themeId);
  const sizePx = LYRIC_MOMENT_LYRIC_PX * frame.factors.display * type.display.scale;
  const scale = width / CANVAS.width;
  const lineStyle: CSSProperties = {
    fontFamily: kitFontName(type.fonts.display),
    fontWeight: type.display.weight,
    fontStyle: type.display.italic ? 'italic' : 'normal',
    letterSpacing: `${type.display.tracking}em`,
    textTransform: 'none',
    fontSize: sizePx,
    lineHeight: 1.04,
    textAlign: 'center',
    textWrap: 'balance',
    textShadow: storyDisplayShadow(themeId, { ...colours, ink: colours.background }, 1),
  };
  const current = lyric?.current ?? '';
  const next = lyric?.next ?? '';

  return (
    <div style={{ position: 'relative', width, height: CANVAS.height * scale }} data-testid="lyric-moment-preview">
      <StoryPreview
        story={story}
        look={look}
        palette={palette}
        storyStyle="card"
        artworkUrl={context.artworkUrl}
        logoUrl={null}
        watermark={context.watermark}
        width={width}
      />
      <div
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          width: CANVAS.width,
          height: CANVAS.height,
          transform: `scale(${scale})`,
          transformOrigin: '0 0',
          pointerEvents: 'none',
          color: colours.text,
        }}
      >
        <div
          data-testid="lyric-moment-preview-sung"
          style={{
            ...lineStyle,
            position: 'absolute',
            left: LYRIC_MOMENT_COLUMN.x,
            width: LYRIC_MOMENT_COLUMN.w,
            bottom: CANVAS.height - LYRIC_MOMENT_COLUMN.sungBottom,
            maxHeight: LYRIC_MOMENT_COLUMN.sungBottom - LYRIC_MOMENT_COLUMN.top,
            overflow: 'hidden',
          }}
        >
          {current}
        </div>
        {next.length > 0 && (
          <div
            style={{
              ...lineStyle,
              position: 'absolute',
              left: LYRIC_MOMENT_COLUMN.x,
              width: LYRIC_MOMENT_COLUMN.w,
              top: LYRIC_MOMENT_COLUMN.sungBottom + LYRIC_MOMENT_COLUMN.gap,
              maxHeight: LYRIC_MOMENT_COLUMN.bottom - LYRIC_MOMENT_COLUMN.sungBottom - LYRIC_MOMENT_COLUMN.gap,
              overflow: 'hidden',
              opacity: motion.nextAlpha,
              transform: `scale(${motion.dimScale})`,
              transformOrigin: '50% 0',
            }}
          >
            {next}
          </div>
        )}
      </div>
    </div>
  );
};

export const LyricMomentPreview = TypedMemo(LyricMomentPreviewComponent);
